import { Outlet } from 'react-router'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'

import useIsExpanded from '@hooks/useIsExpanded'

import SideNav, { SIDE_NAV_WIDTH } from './SideNav'
import TabBar from './TabBar'

AppShell.propTypes = {
  /** `auto` gives the screen navigation; `none` is for the flows that fill the window */
  nav: PropTypes.oneOf(['auto', 'none']),

  /** The routed screen */
  children: PropTypes.node,
}

/**
 * The shell every route sits in: navigation, and the viewport box that makes room for it.
 *
 * A bottom tab bar on a phone, persistent side navigation once there is room for it, and
 * neither inside the native shell, which draws its own. It renders nothing else — the bar,
 * the `main` landmark and the content column all belong to `Screen`.
 *
 * @param {object} props - The component props.
 * @returns {JSX.Element} The rendered shell.
 */
export default function AppShell({ nav = 'auto', children }) {
  const expanded = useIsExpanded() && window.isNativeApp !== true
  const navigating = nav === 'auto'

  return (
    <>
      {navigating && expanded && <SideNav />}

      <Box sx={styles.viewport(navigating && expanded)}>{children || <Outlet />}</Box>

      {navigating && !expanded && <TabBar />}
    </>
  )
}

const styles = {
  // A minimum, never a height: a screen that has to fill the window says so itself, and one
  // capped from up here could not scroll the document at all.
  viewport: (expanded) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100dvh',
    // `clip` rather than `hidden`, which would create a scroll container and break sticky
    overflowX: 'clip',
    ml: expanded ? `${SIDE_NAV_WIDTH}px` : 0,
  }),
}
